import type { GpuEngine } from '../gpu/engine';
import { negotiateVideoMime } from '../utils/mimeNegotiate';
import type { RecordedVideo } from './video';
import { logger } from '../utils/log';

const log = logger('boomerang');

const DEFAULT_DURATION_MS = 1500;
const DEFAULT_FPS = 24;

export interface RecordBoomerangOptions {
  /** The GPU engine's canvas — we read its size and feed pixels back into a
   *  recording canvas of the same dimensions. */
  canvas: HTMLCanvasElement;
  /** Used to snapshot frames synchronously with the render loop so we don't
   *  hit the preserveDrawingBuffer=false → black-frame problem. */
  engine: GpuEngine;
  durationMs?: number;
  fps?: number;
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}

/**
 * Capture a short forward window of frames from the engine canvas, then
 * replay them forward + reverse into a recording canvas to produce a
 * "boomerang" loop. Encoded with the same negotiated MIME (mp4/webm) as
 * the existing video flow so the upload path doesn't change.
 *
 * Memory is bounded by `durationMs * fps` ImageBitmap snapshots — at the
 * default 1.5s × 24fps that's 36 frames.
 */
export async function recordBoomerang(
  opts: RecordBoomerangOptions,
): Promise<RecordedVideo> {
  const {
    canvas,
    engine,
    durationMs = DEFAULT_DURATION_MS,
    fps = DEFAULT_FPS,
    onProgress,
    signal,
  } = opts;
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder not supported in this browser');
  }
  const mime = negotiateVideoMime();
  if (!mime) {
    throw new Error('No supported video mime type for MediaRecorder');
  }
  const frameInterval = 1000 / fps;
  const frameCount = Math.max(2, Math.round((durationMs / 1000) * fps));

  log.info('start', {
    canvasSize: `${canvas.width}x${canvas.height}`,
    durationMs,
    fps,
    frameCount,
    mime: mime.mime,
  });

  // -- Phase 1: capture forward frames via the engine's captureNext, which
  // resolves inside the render tick. createImageBitmap(canvas) on a WebGL
  // canvas with preserveDrawingBuffer=false would just read black.
  const frames: ImageBitmap[] = [];
  const cleanupFrames = () => {
    for (const f of frames) {
      try {
        f.close();
      } catch {
        /* ignore */
      }
    }
    frames.length = 0;
  };

  try {
    const captureStart = performance.now();
    for (let i = 0; i < frameCount; i++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const blob = await engine.captureNext();
      const bm = await createImageBitmap(blob);
      frames.push(bm);
      onProgress?.((i + 1) / (frameCount * 2));
      // Pace ourselves to roughly `fps` so we sample evenly across the
      // duration window even if the engine is rendering faster.
      const target = (i + 1) * frameInterval;
      const elapsed = performance.now() - captureStart;
      const wait = target - elapsed;
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    }
    log.info('captured frames', {
      count: frames.length,
      elapsedMs: Math.round(performance.now() - captureStart),
      avgFrameBytes: frames.length > 0 ? Math.round(
        ((frames[0]!.width * frames[0]!.height * 4) / 1024)
      ) : 0,
      bitmapSize: frames[0] ? `${frames[0].width}x${frames[0].height}` : 'n/a',
    });

    // -- Phase 2: set up recording canvas + recorder --
    const recCanvas = document.createElement('canvas');
    recCanvas.width = frames[0]?.width ?? canvas.width;
    recCanvas.height = frames[0]?.height ?? canvas.height;
    const ctx = recCanvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas unavailable for boomerang');
    // Paint the first frame before captureStream — Safari can otherwise emit
    // an initial black frame.
    ctx.drawImage(frames[0]!, 0, 0);
    const stream = recCanvas.captureStream(fps);
    const recorder = new MediaRecorder(stream, { mimeType: mime.mime });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };

    const start = performance.now();
    const stopPromise = new Promise<RecordedVideo>((resolve, reject) => {
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime.mime });
        log.info('recorder stopped', {
          chunks: chunks.length,
          blobBytes: blob.size,
          mime: mime.mime,
          recDurationMs: Math.round(performance.now() - start),
        });
        resolve({
          blob,
          mimeType: mime.mime,
          durationMs: performance.now() - start,
        });
      };
      recorder.onerror = (ev) =>
        reject(new Error((ev as ErrorEvent).message || 'MediaRecorder error'));
    });
    recorder.start(100);
    log.info('recorder started', { recSize: `${recCanvas.width}x${recCanvas.height}` });

    // -- Phase 3: replay forward + reverse --
    const playFrame = async (bm: ImageBitmap, idx: number, total: number) => {
      ctx.drawImage(bm, 0, 0);
      onProgress?.(0.5 + (idx + 1) / (total * 2));
      await new Promise((r) => setTimeout(r, frameInterval));
    };
    const total = frameCount * 2;
    for (let i = 1; i < frames.length; i++) {
      if (signal?.aborted) break;
      await playFrame(frames[i]!, i - 1, total);
    }
    for (let i = frames.length - 1; i >= 0; i--) {
      if (signal?.aborted) break;
      await playFrame(frames[i]!, frameCount - 1 + (frames.length - 1 - i), total);
    }
    // Settle a final frame so the recorder closes cleanly.
    await new Promise((r) => setTimeout(r, frameInterval * 2));
    log.info('playback done, stopping recorder');
    recorder.stop();

    return await stopPromise;
  } finally {
    cleanupFrames();
  }
}
